import React, { isValidElement, JSX, useContext, useEffect, useState } from 'react'
import '../v2/MailingAddress/index.scss'
import { StoresContext } from '@App/contexts'
import { useFormContext } from 'react-hook-form'
import {
    MailingAddressFormProps,
    markFieldsAsRequired,
    modifyMailingAddressFormProps,
} from '@App/forms/common/mailing-address-form'
import FormFieldFromProps from '@App/kit/fields/FormFieldFromProps'
import { isCountryCAN, isCountryUS, isPostcodeRequiredCountry } from '@App/common/utils'
import { observer } from 'mobx-react'

interface MailingAddressProps {
    title?: string
    toolTip?: JSX.Element
    mailingAddressFormProps: MailingAddressFormProps
    optional?: boolean
    subSection?: JSX.Element
    prefix?: string
}

const MailingAddress = (props: MailingAddressProps) => {
    const { libStore } = useContext(StoresContext)
    const { watch } = useFormContext()
    const { title = 'Mailing Address', toolTip, subSection, optional } = props
    const [mailingAddressFormProps, setMailingAddressFormProps] = useState<MailingAddressFormProps>(
        props.mailingAddressFormProps
    )
    const [addressIsOptional, setAddressIsOptional] = useState<boolean>(!!optional)

    const countryValue = watch(mailingAddressFormProps.country.name)

    useEffect(() => {
        const isUSA = isCountryUS(countryValue)
        const isCanada = isCountryCAN(countryValue)
        const formFields = modifyMailingAddressFormProps(
            mailingAddressFormProps,
            isPostcodeRequiredCountry(countryValue),
            isUSA,
            isCanada,
            libStore.statesForCountryForForm(countryValue)
        )
        setMailingAddressFormProps({ ...formFields })
    }, [countryValue])

    if (optional) {
        const cityValue = watch(mailingAddressFormProps.city.name)
        const streetValue = watch(mailingAddressFormProps.streetAddress.name)
        const unitValue = watch(mailingAddressFormProps.unitNumber.name)
        const stateValue = watch(mailingAddressFormProps.state.name)
        const postalCodeValue = watch(mailingAddressFormProps.postalCode.name)
        useEffect(() => {
            const isThereAddressValue = !!cityValue || !!streetValue || !!unitValue || !!stateValue || !!postalCodeValue
            setAddressIsOptional(!isThereAddressValue)
        }, [cityValue, streetValue, unitValue, stateValue, postalCodeValue, countryValue])

        useEffect(() => {
            const formFields = markFieldsAsRequired(mailingAddressFormProps, addressIsOptional)
            setMailingAddressFormProps({ ...formFields })
        }, [addressIsOptional])
    }

    const { streetAddress, unitNumber, attention, city, state, postalCode, country } = mailingAddressFormProps
    country.options = libStore.countries

    return (
        <div className="mailing-address-wrapper">
            <h5>
                {title}
                {isValidElement(toolTip) && toolTip}
            </h5>
            {subSection && subSection}
            <div className="mailing-address">
                <FormFieldFromProps {...streetAddress} />
                <FormFieldFromProps {...unitNumber} />
                {!!attention && <FormFieldFromProps {...attention} />}
                <FormFieldFromProps {...city} />
                <FormFieldFromProps {...country} />
                <FormFieldFromProps {...state} />
                <FormFieldFromProps {...postalCode} />
            </div>
        </div>
    )
}

export default observer(MailingAddress)
